import { supabase } from "@/integrations/supabase/client";

export interface AICharacter {
    id: string;
    user_id: string;
    alias: string;
    character_id: string;
    name?: string;
    metadata?: any;
    preview_url?: string;
    created_at?: string;
}

export const characterService = {
    async getMyCharacters() {
        const { data, error } = await supabase
            .from("ai_characters" as any)
            .select("*")
            .order("created_at", { ascending: false });

        if (error) throw error;
        return data as any as AICharacter[];
    },

    async saveCharacter(character: Omit<AICharacter, "id" | "user_id" | "created_at">) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("User not authenticated");

        const { data, error } = await supabase
            .from("ai_characters" as any)
            .upsert({
                ...character,
                user_id: user.id,
                updated_at: new Date().toISOString(),
            }, {
                onConflict: 'user_id,alias'
            })
            .select()
            .single();

        if (error) throw error;
        return data as any as AICharacter;
    },

    async deleteCharacter(id: string) {
        const { error } = await supabase
            .from("ai_characters" as any)
            .delete()
            .eq("id", id);

        if (error) throw error;
    },

    async getCharacterByAlias(alias: string) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return null;

        const { data, error } = await supabase
            .from("ai_characters" as any)
            .select("*")
            .eq("user_id", user.id)
            .eq("alias", alias)
            .maybeSingle();

        if (error) return null;
        return data as any as AICharacter;
    },

    /**
     * Translates aliases (@@name) in a prompt to their respective character_ids
     * Returns the translated prompt and if found, the first character_id detected
     */
    async resolvePromptAliases(prompt: string) {
        const aliasRegex = /@@(\w+)/g;
        const matches = Array.from(prompt.matchAll(aliasRegex));

        if (matches.length === 0) return { prompt, characterId: null };

        let resolvedPrompt = prompt;
        let mainCharacterId: string | null = null;

        for (const match of matches) {
            const alias = match[0];
            const char = await this.getCharacterByAlias(alias);
            if (char) {
                if (!mainCharacterId) mainCharacterId = char.character_id;
                // Strip the alias from the prompt for the AI to handle it as action
                resolvedPrompt = resolvedPrompt.replace(alias, "").replace(/\s+/g, ' ').trim();
            }
        }

        return { prompt: resolvedPrompt, characterId: mainCharacterId };
    }
};
